import React, {useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import Select from 'react-select'
import {profession} from '../../data/constantData'
import {
  getRangeForPreset,
  toDateInputValue,
} from '../wallet/walletDateRange'
import {
  USER_DATE_PRESET_OPTIONS,
  USER_ROLE_OPTIONS,
  UserDatePreset,
  UserFilterState,
} from './userDateRange'

type Props = {
  showFilter: boolean
  toggle: () => void
  activeFilter: UserFilterState
  onApply: (filter: UserFilterState) => void
}

const FilterUsers = ({showFilter, toggle, activeFilter, onApply}: Props) => {
  const [preset, setPreset] = useState<UserDatePreset>(activeFilter.preset)
  const [customFrom, setCustomFrom] = useState(activeFilter.date_from)
  const [customTo, setCustomTo] = useState(activeFilter.date_to)
  const [selectedProfession, setSelectedProfession] = useState<{label: string} | null>(
    activeFilter.profession ? {label: activeFilter.profession} : null
  )
  const [roleKey, setRoleKey] = useState(
    USER_ROLE_OPTIONS.find((o) => o.roleId === activeFilter.role)?.value ?? ''
  )
  const [rangeError, setRangeError] = useState('')

  useEffect(() => {
    if (!showFilter) return
    setPreset(activeFilter.preset)
    setCustomFrom(activeFilter.date_from || toDateInputValue(new Date()))
    setCustomTo(activeFilter.date_to || toDateInputValue(new Date()))
    setSelectedProfession(
      activeFilter.profession ? {label: activeFilter.profession} : null
    )
    setRoleKey(USER_ROLE_OPTIONS.find((o) => o.roleId === activeFilter.role)?.value ?? '')
    setRangeError('')
  }, [showFilter, activeFilter])

  const handlePresetChange = (id: UserDatePreset) => {
    setPreset(id)
    setRangeError('')
    if (id !== 'custom') {
      const range = getRangeForPreset(id)
      setCustomFrom(range.date_from)
      setCustomTo(range.date_to)
    }
  }

  const handleFilterData = (e: React.FormEvent) => {
    e.preventDefault()
    if (preset === 'custom' && customFrom && customTo && customFrom > customTo) {
      setRangeError('From date must be on or before To date.')
      return
    }
    const range = getRangeForPreset(
      preset,
      preset === 'custom' ? customFrom : undefined,
      preset === 'custom' ? customTo : undefined
    )
    const roleOption = USER_ROLE_OPTIONS.find((o) => o.value === roleKey)
    onApply({
      ...range,
      profession: selectedProfession?.label ?? '',
      role: roleOption?.roleId ?? null,
    })
    toggle()
  }

  return (
    <Modal show={showFilter} className='modal-md' onHide={toggle}>
      <Modal.Header className='bg-primary-subtle' closeButton>
        <Modal.Title>Filter users</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleFilterData}>
          <label className='form-label fw-semibold'>Date range</label>
          <select
            className='form-select mb-4'
            value={preset}
            onChange={(e) => handlePresetChange(e.target.value as UserDatePreset)}
          >
            {USER_DATE_PRESET_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>

          {preset === 'custom' && (
            <div className='row g-4 mb-4'>
              <div className='col-6'>
                <label className='form-label'>From</label>
                <input
                  type='date'
                  className='form-control'
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => {
                    setCustomFrom(e.target.value)
                    setRangeError('')
                  }}
                />
              </div>
              <div className='col-6'>
                <label className='form-label'>To</label>
                <input
                  type='date'
                  className='form-control'
                  value={customTo}
                  min={customFrom || undefined}
                  max={toDateInputValue(new Date())}
                  onChange={(e) => {
                    setCustomTo(e.target.value)
                    setRangeError('')
                  }}
                />
              </div>
              {rangeError ? (
                <div className='col-12 text-danger fs-7'>{rangeError}</div>
              ) : null}
            </div>
          )}

          <label className='form-label fw-semibold'>Profession</label>
          <Select
            options={profession}
            value={selectedProfession}
            onChange={(option: {label: string} | null) => setSelectedProfession(option)}
            isClearable
            className='mb-4'
            placeholder='All professions'
          />

          <label className='form-label fw-semibold'>Role</label>
          <select
            className='form-select mb-4'
            value={roleKey}
            onChange={(e) => setRoleKey(e.target.value)}
          >
            {USER_ROLE_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <Modal.Footer className='px-0 pb-0'>
            <Button variant='secondary' size='sm' onClick={toggle}>
              Close
            </Button>
            <Button type='submit' variant='primary' size='sm'>
              Apply
            </Button>
          </Modal.Footer>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default FilterUsers
